import { AxiosInstance } from 'axios';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import {
  DeviceAvailabilityCalendarPoint,
  DeviceAvailabilityCalendarQuery,
  DevicePmpBooking,
  DevicePmpBookingQuery,
  DeviceAvailabilityList,
  DeviceAvailabilityQuery,
  DeviceAvailabilityRecord,
  DeviceAvailabilitySummary,
  DeviceChannel,
  DeviceDaypartsParam,
  DeviceUnitDetail,
  DeviceUnitExportQuery,
  DeviceUnitList,
  DeviceUnitQuery
} from 'core/deviceManagement/DeviceManagement';
import client from './RestClient';

export interface DeviceManagementWebService {
  getChannels(): Promise<DeviceChannel[]>;
  getUnits(query: DeviceUnitQuery): Promise<DeviceUnitList>;
  getUnitDetail(channelId: number, unitKey: string): Promise<DeviceUnitDetail>;
  /** 檔名以回應的 Content-Disposition 為準，讀不到才用 fallbackFileName。 */
  exportUnits(
    query: DeviceUnitExportQuery,
    fallbackFileName: string
  ): Promise<void>;
  getAvailability(
    query: DeviceAvailabilityQuery
  ): Promise<DeviceAvailabilityList>;
  getAvailabilityCalendar(
    query: DeviceAvailabilityCalendarQuery
  ): Promise<DeviceAvailabilityCalendarPoint[]>;
  getPmpBookings(query: DevicePmpBookingQuery): Promise<DevicePmpBooking[]>;
}

/**
 * 清單型條件的預設值。
 *
 * 四個條件即使沒選也照樣送一個空陣列出去：空陣列與欄位缺席對後端是同一件事
 * （都代表「沒有這個條件」，不是「限定在空集合裡」），而固定出現的欄位讀起來
 * 才看得出送出去的到底有哪幾種條件。
 */
const EMPTY_LIST_CONDITIONS: Record<string, string[]> = {
  regions: [],
  districts: [],
  placeCategories: [],
  healthStates: []
};

/**
 * 清單、匯出與可售率三支共用的查詢條件。
 *
 * `filter` 的 key 已經是後端收的欄位名（見 containers/DeviceManagement/deviceFilters.ts
 * 的 DEVICE_FILTER_WIRE_PARAM），所以直接攤進 body，不在這一層再換一次名字。
 *
 * 匯入名單的店號同樣一律帶：它與其他條件是疊加的，少送一邊回來的就是另一份母體。
 */
const toUnitSearchBody = (query: Omit<DeviceUnitQuery, 'page' | 'size'>) => ({
  channelId: query.channelId,
  ...EMPTY_LIST_CONDITIONS,
  ...query.filter,
  storeNums: query.storeNums,
  q: query.search
});

/**
 * 分頁與排序留在 query string，body 只放查詢條件。
 *
 * 線上的頁碼是 1 起算，不是 Spring 原生的 0 起算：這個服務的
 * `PageableHandlerMethodArgumentResolver` 全域開了 `setOneIndexedParameters(true)`，
 * 會把收到的值減一。送 0 起算的話 `0` 與 `1` 都會解成第一頁，使用者翻到第二頁
 * 看到的是第一頁的內容，而且往後每一頁都差一頁 —— 畫面上完全看不出來。
 * repo 內其他清單也都是送 1 起算。
 *
 * 模型內部維持 0 起算（表格元件那一側自己再換一次），只有出門這一步加一。
 */
const toPageParams = (query: DeviceUnitQuery) => {
  const params = new URLSearchParams();
  params.append('page', String(query.page + 1));
  params.append('size', String(query.size));
  params.append('sort', `${query.sortField},${query.sortOrder}`);
  return params.toString();
};

/**
 * 投放時段是一個 `星期 → 小時陣列` 的物件，整包序列化成一個 JSON 字串送出。
 *
 * 後端唯一的驗證入口讀的是這個字串（「key 不是 1 到 7 就回 400」那條規則也落在那裡），
 * 所以放進 body 的時候也維持字串，不改成巢狀物件 —— 換成物件等於要求後端把那段驗證
 * 再寫一份。攤平成重複參數（`dayparts=1:0&dayparts=1:1`）則會讓「星期六整天都沒選」
 * 與「沒帶這個條件」在線上長得一樣。
 */
const serializeDayparts = (dayparts: DeviceDaypartsParam) =>
  JSON.stringify(dayparts);

const appendDayparts = (
  params: URLSearchParams,
  dayparts: DeviceDaypartsParam
) => {
  params.append('dayparts', serializeDayparts(dayparts));
};

/**
 * 後端沒給、或給的不是一個有限的數字時，一律折成 null。
 *
 * 這一層是唯一擋得住的位置。少了它，缺席的欄位會以 `undefined` 一路流到顯示層，
 * 而顯示層擋的是 `=== null`：`undefined * 100` 算出 NaN，畫面上就寫著「NaN%」，
 * 而型別上那個欄位宣告的是 `number`，tsc 一個字都不會說。
 * 只在顯示層補一個 isNaN 治不了它 —— 下一個欄位會再出現一次。
 *
 * 折成 null 而不是 0：null 是「這個數字問不到」，0 是「數字就是 0」，
 * 對業務而言兩者意思相反（「可售 0 秒」是一句假話）。
 */
const toNullableNumber = (value: any): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** 型別上是選填而不是可為 null 的數值欄位，同一條規則，只是缺席寫成 undefined。 */
const toOptionalNumber = (value: any): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const wrapAvailabilitySummary = (json: any): DeviceAvailabilitySummary => ({
  deliverableUnitCount: toNullableNumber(json?.deliverableUnitCount),
  totalUnitCount: toNullableNumber(json?.totalUnitCount),
  overallAvailabilityRate: toNullableNumber(json?.overallAvailabilityRate),
  totalSellableSeconds: toNullableNumber(json?.totalSellableSeconds),
  totalSoldSeconds: toNullableNumber(json?.totalSoldSeconds),
  totalAvailableSeconds: toNullableNumber(json?.totalAvailableSeconds),
  missingBusinessHoursUnitCount: toNullableNumber(
    json?.missingBusinessHoursUnitCount
  ),
  uncomputableCampaignCount: toNullableNumber(json?.uncomputableCampaignCount)
});

const wrapAvailabilityRecord = (json: any): DeviceAvailabilityRecord => ({
  ...json,
  dailyAverageFootfall: toNullableNumber(json?.dailyAverageFootfall),
  totalSellableSeconds: toNullableNumber(json?.totalSellableSeconds),
  soldSeconds: toNullableNumber(json?.soldSeconds),
  availableSeconds: toNullableNumber(json?.availableSeconds),
  availabilityRate: toNullableNumber(json?.availabilityRate)
});

/**
 * 從 `Content-Disposition` 取出檔名，取不到回 undefined。
 *
 * 兩種寫法都要認：`filename="x.csv"`，以及 RFC 5987 的
 * `filename*=UTF-8''%E6%A9%9F%E5%8F%B0.csv`。兩者同時出現時以編碼過的那一個為準，
 * 只有它表達得了非 ASCII 的檔名。
 */
export const parseContentDispositionFileName = (
  contentDisposition?: string
): string | undefined => {
  if (!contentDisposition) {
    return undefined;
  }
  const extended = /filename\*\s*=\s*([^;]+)/i.exec(contentDisposition);
  if (extended) {
    const value = extended[1].trim();
    // 格式是 <charset>'<language>'<百分比編碼的值>，語系可以是空的。
    const segments = value.split("'");
    const encoded = segments.length >= 3 ? segments.slice(2).join("'") : value;
    try {
      const decoded = decodeURIComponent(encoded);
      if (decoded !== '') {
        return decoded;
      }
    } catch {
      // 值裡有落單的 `%` 時 decodeURIComponent 會丟例外，改用下面沒編碼的那一個。
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]*))/i.exec(
    contentDisposition
  );
  const name = plain ? (plain[1] ?? plain[2] ?? '').trim() : '';
  return name === '' ? undefined : name;
};

export class RestfulDeviceManagementWebService implements DeviceManagementWebService {
  restClient: AxiosInstance;

  constructor(
    restClient: AxiosInstance = client,
    private defaultFileDownload: any = fileDownload
  ) {
    this.restClient = restClient;
  }

  async getChannels(): Promise<DeviceChannel[]> {
    const response = await this.restClient.get(
      '/v2/device-management/channels'
    );
    return _.defaultTo(response.data.records, []);
  }

  async getUnits(query: DeviceUnitQuery): Promise<DeviceUnitList> {
    const response = await this.restClient.post(
      `/v2/device-management/units/search?${toPageParams(query)}`,
      toUnitSearchBody(query)
    );
    return {
      records: _.defaultTo(response.data.records, []),
      pagination: response.data.pagination,
      lastSyncedAt: response.data.lastSyncedAt
    };
  }

  async getAvailability(
    query: DeviceAvailabilityQuery
  ): Promise<DeviceAvailabilityList> {
    const response = await this.restClient.post(
      `/v2/device-management/availability/search?${toPageParams(query)}`,
      {
        ...toUnitSearchBody(query),
        startDate: query.startDate,
        endDate: query.endDate,
        dayparts: serializeDayparts(query.dayparts)
      }
    );
    return {
      records: _.defaultTo(response.data.records, []).map(
        wrapAvailabilityRecord
      ),
      pagination: response.data.pagination,
      summary: wrapAvailabilitySummary(response.data.summary),
      lastSyncedAt: response.data.lastSyncedAt
    };
  }

  async getAvailabilityCalendar(
    query: DeviceAvailabilityCalendarQuery
  ): Promise<DeviceAvailabilityCalendarPoint[]> {
    // unitKey 的值本身含有 `/`，所以跟詳情一樣走 query parameter。
    const params = new URLSearchParams();
    params.append('channelId', String(query.channelId));
    params.append('unitKey', query.unitKey);
    params.append('startDate', query.startDate);
    params.append('endDate', query.endDate);
    appendDayparts(params, query.dayparts);
    const response = await this.restClient.get(
      `/v2/device-management/availability/calendar?${params.toString()}`
    );
    return _.defaultTo(response.data.records, []).map((point: any) => ({
      date: point?.date,
      availabilityRate: toNullableNumber(point?.availabilityRate),
      businessHoursProvenance: point?.businessHoursProvenance
    }));
  }

  async getPmpBookings(
    query: DevicePmpBookingQuery
  ): Promise<DevicePmpBooking[]> {
    // 與月曆同一組理由：unitKey 含 `/`，所以走 query parameter。
    const params = new URLSearchParams({
      channelId: String(query.channelId),
      unitKey: query.unitKey,
      startDate: query.startDate,
      endDate: query.endDate
    });
    const response = await this.restClient.get(
      `/v2/device-management/units/pmp-bookings?${params.toString()}`
    );
    return _.defaultTo(response.data.records, []).map((booking: any) => ({
      orderName: _.defaultTo(booking?.orderName, null),
      startTime: _.defaultTo(booking?.startTime, null),
      endTime: _.defaultTo(booking?.endTime, null),
      agencyName: _.defaultTo(booking?.agencyName, null)
    }));
  }

  async getUnitDetail(
    channelId: number,
    unitKey: string
  ): Promise<DeviceUnitDetail> {
    // unitKey 的值本身含有 `/`，所以是 query parameter 而不是 path variable。
    // 同一個 unitKey 可能同時存在於多個渠道，而且要聚合哪些版位取決於渠道的
    // 投遞單位，所以 channelId 必須一起帶。
    const params = new URLSearchParams({
      channelId: String(channelId),
      unitKey
    });
    const response = await this.restClient.get(
      `/v2/device-management/units/detail?${params.toString()}`
    );
    return {
      ...response.data,
      placeCategoryLabels: _.defaultTo(response.data.placeCategoryLabels, []),
      last7DaysFootfall: _.defaultTo(response.data.last7DaysFootfall, []).map(
        (point: any) => ({
          date: point?.date,
          footfall: toNullableNumber(point?.footfall)
        })
      ),
      dailyAverageFootfall: toNullableNumber(
        response.data.dailyAverageFootfall
      ),
      latitude: toOptionalNumber(response.data.latitude),
      longitude: toOptionalNumber(response.data.longitude),
      screenPixelWidth: toOptionalNumber(response.data.screenPixelWidth),
      screenPixelHeight: toOptionalNumber(response.data.screenPixelHeight)
    };
  }

  async exportUnits(
    query: DeviceUnitExportQuery,
    fallbackFileName: string
  ): Promise<void> {
    // 匯出沒有分頁，但排序與清單同一套：匯出的是「畫面上看到的那一份」。
    const params = new URLSearchParams();
    params.append('sort', `${query.sortField},${query.sortOrder}`);
    const response = await this.restClient.post(
      `/v2/device-management/units/export?${params.toString()}`,
      {
        ...toUnitSearchBody(query),
        // 不給 limit 表示全部，這個欄位就整個不出現。
        ...(query.limit === undefined ? {} : { limit: query.limit }),
        // 走期與投放時段只有可售率分頁會帶。那一頁的排序鍵是這兩個條件的函數，
        // 後端要靠它們才算得出可售秒數；清單檢視不帶，那幾個欄位它也排不到。
        ...(query.startDate === undefined
          ? {}
          : { startDate: query.startDate }),
        ...(query.endDate === undefined ? {} : { endDate: query.endDate }),
        ...(query.dayparts === undefined
          ? {}
          : { dayparts: serializeDayparts(query.dayparts) })
      },
      // post 的第三個參數才是設定；擺到第二個會被當成 body，下載回來的是字串而不是
      // Blob，而且兩邊都不會報錯。
      { responseType: 'blob' }
    );
    // 檔案由後端產生，前端只負責存檔；CSV 的 BOM 也在後端那一側加
    // （見 utils/DownloadUtils.ts 的 downloadCsv，那一支才是前端自己組字串的路徑）。
    //
    // 檔名一樣以後端為準：它組出來的名字帶著這次的排序與篩選條件，而且刻意不帶時間戳，
    // 同一組條件匯出幾次都會拿到同一個檔名。前端自己組一個會把這件事整個蓋掉。
    // responseType 是 blob 只影響 body，header 照樣讀得到。
    //
    // fallback 不是裝飾：跨網域時瀏覽器只交出 CORS `Access-Control-Expose-Headers`
    // 列到的 header，Content-Disposition 沒被列到就讀不到，那時只剩前端這個名字。
    const headerFileName = parseContentDispositionFileName(
      response.headers?.['content-disposition']
    );
    this.defaultFileDownload(response.data, headerFileName ?? fallbackFileName);
  }
}
